/**
 * CancellationPolicy Component
 *
 * Displays cancellation policy details for a booking.
 */

import React from 'react';

interface CancellationPolicyProps {
  policy: 'flexible' | 'moderate' | 'strict';
  checkIn?: Date;
}

interface PolicyDetails {
  name: string;
  description: string;
  refundTerms: Array<{
    deadline: string;
    refund: string;
    description: string;
  }>;
}

const POLICIES: Record<string, PolicyDetails> = {
  flexible: {
    name: 'Flexible',
    description: 'Full refund up to 24 hours before check-in',
    refundTerms: [
      {
        deadline: 'More than 24 hours before check-in',
        refund: '100%',
        description: 'Full refund of the total booking amount',
      },
      {
        deadline: 'Within 24 hours of check-in',
        refund: '0%',
        description: 'No refund',
      },
      {
        deadline: 'After check-in',
        refund: '0%',
        description: 'No refund for remaining nights',
      },
    ],
  },
  moderate: {
    name: 'Moderate',
    description: 'Full refund up to 5 days before check-in',
    refundTerms: [
      {
        deadline: 'More than 5 days before check-in',
        refund: '100%',
        description: 'Full refund of the total booking amount',
      },
      {
        deadline: '1-5 days before check-in',
        refund: '50%',
        description: '50% refund (cleaning fees not refunded)',
      },
      {
        deadline: 'Within 24 hours of check-in',
        refund: '0%',
        description: 'No refund',
      },
    ],
  },
  strict: {
    name: 'Strict',
    description: '50% refund up to 7 days before check-in',
    refundTerms: [
      {
        deadline: 'More than 7 days before check-in',
        refund: '50%',
        description: '50% refund (service and cleaning fees not refunded)',
      },
      {
        deadline: 'Within 7 days of check-in',
        refund: '0%',
        description: 'No refund',
      },
      {
        deadline: 'After check-in',
        refund: '0%',
        description: 'No refund for remaining nights',
      },
    ],
  },
};

export const CancellationPolicy: React.FC<CancellationPolicyProps> = ({
  policy,
  checkIn: _checkIn,
}) => {
  const policyDetails = POLICIES[policy] || POLICIES.moderate;

  return (
    <div className="space-y-4">
      {/* Policy Header */}
      <div className="flex items-center gap-2">
        <span
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            policy === 'flexible'
              ? 'bg-green-100 text-green-700'
              : policy === 'moderate'
              ? 'bg-amber-100 text-amber-700'
              : 'bg-red-100 text-red-700'
          }`}
        >
          {policyDetails.name}
        </span>
        <span className="text-gray-600">{policyDetails.description}</span>
      </div>

      {/* Refund Terms */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">
                Cancellation Deadline
              </th>
              <th className="text-center px-4 py-3 font-medium text-gray-600 w-24">
                Refund
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {policyDetails.refundTerms.map((term, index) => (
              <tr key={index}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{term.deadline}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{term.description}</div>
                </td>
                <td className="px-4 py-3 text-center">
                  <span
                    className={`font-semibold ${
                      term.refund === '100%'
                        ? 'text-green-600'
                        : term.refund === '50%'
                        ? 'text-amber-600'
                        : 'text-red-600'
                    }`}
                  >
                    {term.refund}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Important Notes */}
      <div className="text-sm text-gray-500 space-y-1">
        <p className="flex items-start gap-2">
          <InfoIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Service fees are refunded when you cancel before check-in and get a full refund.
          </span>
        </p>
        <p className="flex items-start gap-2">
          <InfoIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            If a host cancels, you'll get a full refund regardless of the policy.
          </span>
        </p>
      </div>
    </div>
  );
};

// Info Icon
const InfoIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export default CancellationPolicy;
